import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    ActivatedRoute,
    NavigationEnd,
    NavigationExtras,
    Router,
} from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { filter, Subject, takeUntil } from 'rxjs';
import { fuseAnimations } from '@fuse/animations';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { ShiftDataService } from '../../../../../../app/components/shift-data-analysis/shift-data.service';
import { DashboardService } from 'app/services/dashboard.service';

@Component({
    selector: 'fuse-vertical-navigation-collapsable-item',
    templateUrl: './collapsable.component.html',
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FuseVerticalNavigationCollapsableItemComponent
    implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_autoCollapse: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() autoCollapse: boolean;
    @Input() chainage: boolean;
    @Input() item: FuseNavigationItem;
    @Input() name: string;

    chainageLowValue: any;
    chainageHighValue: any;

    isCollapsed: boolean = true;
    isExpanded: boolean = false;
    private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _fuseNavigationService: FuseNavigationService,
        public shiftDataService: ShiftDataService,
        public dashboardService: DashboardService,
        private route: ActivatedRoute
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'fuse-vertical-navigation-item-collapsed': this.isCollapsed,
            'fuse-vertical-navigation-item-expanded': this.isExpanded,
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Fetching query parameters for chainage values
        this.route.queryParams.subscribe((params) => {
            if (params.chainageLowValue) {
                this.chainageLowValue = +params.chainageLowValue;
            }

            if (params.chainageHighValue) {
                this.chainageHighValue = +params.chainageHighValue;
            }
        });

        // Get the parent navigation component
        this._fuseVerticalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        // If the item has a children that has a matching url with the current url, expand...
        if (this._hasActiveChild(this.item, this._router.url)) {
            this.expand();
        }
        // Otherwise...
        else {
            // If the autoCollapse is on, collapse...
            if (this.autoCollapse) {
                this.collapse();
            }
        }

        // Listen for the onCollapsableItemCollapsed from the service
        this._fuseVerticalNavigationComponent.onCollapsableItemCollapsed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((collapsedItem) => {
                // Check if the collapsed item is null
                if (collapsedItem === null) {
                    return;
                }

                // Collapse if this is a children of the collapsed item
                if (this._isChildrenOf(collapsedItem, this.item)) {
                    this.collapse();
                }
            });

        // this.shiftDataService.chainageValuesChangeEmitter
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe(() => {
        //         this.shiftDataService.chainageLowValue =
        //             this.shiftDataService.defaultMinChainage;
        //         this.shiftDataService.chainageHighValue =
        //             this.shiftDataService.defaultMaxChainage;

        //         this.collapse();
        //     });

        // Listen for the onCollapsableItemExpanded from the service if the autoCollapse is on
        if (this.autoCollapse) {
            this._fuseVerticalNavigationComponent.onCollapsableItemExpanded
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((expandedItem) => {
                    // Check if the expanded item is null
                    if (expandedItem === null) {
                        return;
                    }

                    // Check if this is a parent of the expanded item
                    if (this._isChildrenOf(this.item, expandedItem)) {
                        return;
                    }

                    // Check if this has a children with a matching url with the current active url
                    if (this._hasActiveChild(this.item, this._router.url)) {
                        return;
                    }

                    // Check if this is the expanded item
                    if (this.item === expandedItem) {
                        return;
                    }

                    // If none of the above conditions are matched, collapse this item
                    this.collapse();
                });
        }

        // Attach a listener to the NavigationEnd event
        this._router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd
                ),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                // If the item has a children that has a matching url with the current url, expand...
                if (this._hasActiveChild(this.item, event.urlAfterRedirects)) {
                    this.expand();
                }
                // Otherwise...
                else {
                    // If the autoCollapse is on, collapse...
                    if (this.autoCollapse) {
                        this.collapse();
                    }
                }
            });

        // Subscribe to onRefreshed on the navigation component
        this._fuseVerticalNavigationComponent.onRefreshed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**Keyboard Enter event added-to call onChainageFilterClick() */
    @HostListener('window:keyup', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 13) {
            // this.onChainageFilterClick();
        }
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Collapse
     */
    collapse(): void {
        // Return if the item is disabled
        if (this.item.disabled) {
            return;
        }

        // Return if the item is already collapsed
        if (this.isCollapsed) {
            return;
        }

        // Collapse it
        this.isCollapsed = true;
        this.isExpanded = !this.isCollapsed;

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Execute the observable
        this._fuseVerticalNavigationComponent.onCollapsableItemCollapsed.next(
            this.item
        );
    }

    /**
     * Expand
     */
    expand(): void {
        // Return if the item is disabled
        if (this.item.disabled) {
            return;
        }

        // Return if the item is already expanded
        if (!this.isCollapsed) {
            return;
        }

        // Expand it
        this.isCollapsed = false;
        this.isExpanded = !this.isCollapsed;

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Execute the observable
        this._fuseVerticalNavigationComponent.onCollapsableItemExpanded.next(
            this.item
        );
    }

    /**
     * Toggle collapsable
     */
    toggleCollapsable(): void {
        // Toggle collapse/expand
        if (this.isCollapsed) {
            setTimeout(() => {
                const params = this.route.snapshot.queryParams;
                let currentPage : any = localStorage.getItem('currentPage');
                const selectedSubSite = JSON.parse(this.route.snapshot.queryParams.selectedSubSite);
                if (params.chainageLowValue) {
                    this.chainageLowValue = +params.chainageLowValue;
                    if(currentPage == "WorkOrder" && this.shiftDataService.defaultMinChainage === undefined){
                        this.setMinAndMaxChainages();
                    }
                } 
                else if(selectedSubSite.length == 1 && selectedSubSite.start_chainage){
                    this.chainageLowValue = +selectedSubSite.start_chainage;
                    this.shiftDataService.defaultMinChainage = selectedSubSite.start_chainage;
                }
                else if(selectedSubSite.length == 2 && selectedSubSite[0].start_chainage){
                    this.chainageLowValue = +selectedSubSite[0].start_chainage;
                    this.shiftDataService.defaultMinChainage = selectedSubSite[0].start_chainage;
                }
                else {
                    this.chainageLowValue =
                        +this.shiftDataService.defaultMinChainage;
                }

                if (params.chainageHighValue) {
                    this.chainageHighValue = +params.chainageHighValue;
                    if(currentPage == "WorkOrder" && this.shiftDataService.defaultMaxChainage === undefined){
                        this.setMinAndMaxChainages();
                    }
                } 
                else if(selectedSubSite.length == 1 && selectedSubSite.end_chainage){
                    this.chainageHighValue = +selectedSubSite.end_chainage;
                    this.shiftDataService.defaultMaxChainage = selectedSubSite.end_chainage;
                }
                else if(selectedSubSite.length == 2 && selectedSubSite[0].end_chainage){
                    this.chainageHighValue = +selectedSubSite[0].end_chainage;
                    this.shiftDataService.defaultMaxChainage = selectedSubSite[0].end_chainage;
                }
                else {
                    this.chainageHighValue =
                        +this.shiftDataService.defaultMaxChainage;
                }
                this._changeDetectorRef.markForCheck();
            }, 10);

            // if (!this.chainageLowValue) {
            //     this.chainageLowValue =
            //         this.shiftDataService.defaultMinChainage;
            // }
            // if (!this.chainageHighValue) {
            //     this.chainageHighValue =
            //         this.shiftDataService.defaultMaxChainage;
            // }
            this.expand();
        } else {
            this.collapse();
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check if the given item has the given url
     * in one of its children
     *
     * @param item
     * @param currentUrl
     * @private
     */
    private _hasActiveChild(
        item: FuseNavigationItem,
        currentUrl: string
    ): boolean {
        const children = item.children;

        if (!children) {
            return false;
        }

        for (const child of children) {
            if (child.children) {
                if (this._hasActiveChild(child, currentUrl)) {
                    return true;
                }
            }

            // Check if the child has a link and is active
            if (
                child.link &&
                this._router.isActive(child.link, child.exactMatch || false)
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if this is a children
     * of the given item
     *
     * @param parent
     * @param item
     * @private
     */
    private _isChildrenOf(
        parent: FuseNavigationItem,
        item: FuseNavigationItem
    ): boolean {
        const children = parent.children;

        if (!children) {
            return false;
        }

        if (children.indexOf(item) > -1) {
            return true;
        }

        for (const child of children) {
            if (child.children) {
                if (this._isChildrenOf(child, item)) {
                    return true;
                }
            }
        }

        return false;
    }

    onChainageFilterClick() {
        /**method should be called only when it doesn't satisfy any of these condition,Enter event */
        if(this.chainageHighValue<this.chainageLowValue ||
            this.chainageHighValue>this.shiftDataService.defaultMaxChainage ||
            this.chainageLowValue>this.shiftDataService.defaultMaxChainage||
            this.chainageLowValue<this.shiftDataService.defaultMinChainage){}
        else{
        const queryParams = { ...this.route.snapshot.queryParams };
        queryParams['chainageLowValue'] = this.chainageLowValue;
        queryParams['chainageHighValue'] = this.chainageHighValue;
        const navigationExtras: NavigationExtras = {
            queryParams,
            queryParamsHandling: 'merge',
        };

        this._router.navigate([], navigationExtras);

        this.shiftDataService.onFilterChange.emit();
    }
    }

    onResetChainageFiltersClick() {
        // this.shiftDataService.chainageLowValue =
        //     this.shiftDataService.defaultMinChainage;
        // this.shiftDataService.chainageHighValue =
        //     this.shiftDataService.defaultMaxChainage;
        // this.shiftDataService.onFilterChange.emit();
        this.shiftDataService.removeChainageFilters();
        this.chainageLowValue = this.shiftDataService.defaultMinChainage;
        this.chainageHighValue = this.shiftDataService.defaultMaxChainage;
        this.shiftDataService.onFilterChange.emit();
    }

    onChainageValueChange(value, isMinChainage) {
        const queryParams = { ...this.route.snapshot.queryParams };
        if (isMinChainage) {
            queryParams['chainageLowValue'] = value;
        } else {
            queryParams['chainageHighValue'] = value;
        }
        const navigationExtras: NavigationExtras = {
            queryParams,
            queryParamsHandling: 'merge',
        };

        this._router.navigate([], navigationExtras);
    }

    setMinAndMaxChainages() {
        if (this.shiftDataService.selectedSubSite.length > 1) {
            this.shiftDataService.defaultMinChainage = this.shiftDataService.selectedSubSite[0].start_chainage;
            this.shiftDataService.defaultMaxChainage = this.shiftDataService.selectedSubSite[0].end_chainage;
        }
        else {
            this.shiftDataService.defaultMinChainage = this.shiftDataService.selectedSubSite.start_chainage;
            this.shiftDataService.defaultMaxChainage = this.shiftDataService.selectedSubSite.end_chainage;
        }
    }
}
